import React, { useState } from 'react'

export default function ContactForm({ subject = 'Generel henvendelse' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  })
  const [errors, setFieldErrors] = useState({})
  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Navn skal udfyldes'
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes'
    } else if (!/^\+?[0-9\s]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse'
    }
    if (!formData.description.trim()) newErrors.description = 'Beskrivelse skal udfyldes'
    
    setFieldErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          subject: subject
        })
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        })
      } else {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(err.message || 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.')
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Skriv til Frejlev Auto</h3>
      <p className="text-gray-600 text-sm mb-6 leading-relaxed">
        Udfyld formularen nedenfor, og vi vender tilbage til dig hurtigst muligt.
      </p>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-[#5cbe4f] mb-4">
            <i className="fa-solid fa-check text-xl"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">Besked modtaget!</h4>
          <p className="text-gray-600 text-sm">
            Tak for din henvendelse. Vi har modtaget din besked og kontakter dig hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 px-5 py-2.5 bg-[#5cbe4f] hover:bg-[#4ca83e] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot Field - Absolutely Hidden */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Navn <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className={`w-full bg-gray-50 border ${errors.name ? 'border-red-500 focus:ring-red-500/20' : 'border-gray-300 focus:ring-[#5cbe4f]/20 focus:border-[#5cbe4f]'} rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-4 transition-all`}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Tlf. nummer"
                className={`w-full bg-gray-50 border ${errors.phone ? 'border-red-500 focus:ring-red-500/20' : 'border-gray-300 focus:ring-[#5cbe4f]/20 focus:border-[#5cbe4f]'} rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-4 transition-all`}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Din e-mailadresse"
                className={`w-full bg-gray-50 border ${errors.email ? 'border-red-500 focus:ring-red-500/20' : 'border-gray-300 focus:ring-[#5cbe4f]/20 focus:border-[#5cbe4f]'} rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-4 transition-all`}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Beskrivelse / Besked <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Skriv din besked her..."
              className={`w-full bg-gray-50 border ${errors.description ? 'border-red-500 focus:ring-red-500/20' : 'border-gray-300 focus:ring-[#5cbe4f]/20 focus:border-[#5cbe4f]'} rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-4 transition-all resize-none`}
            ></textarea>
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>

          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-xs flex items-start gap-2">
              <i className="fa-solid fa-triangle-exclamation mt-0.5"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-[#5cbe4f] hover:bg-[#4ca83e] disabled:bg-gray-300 disabled:text-gray-500 text-white font-bold text-sm py-3.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-[#5cbe4f]/20 uppercase tracking-wider"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-circle-notch animate-spin"></i>
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i>
                <span>Send besked</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
